import React, { useCallback, useEffect, useState } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ThemeProvider,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    CssBaseline,
    Badge,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, Link, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout as logoutAction } from '../slices/authSlice';
import { hasAdminPrivileges } from '../utils/permissionsClient';
import AccountCircle from '@mui/icons-material/AccountCircle';
import TranslateIcon from '@mui/icons-material/Translate';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';
import BuildIcon from '@mui/icons-material/Build';
import ChatIcon from '@mui/icons-material/Chat';
import AppsIcon from '@mui/icons-material/Apps';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import theme from '../theme';
import axios from 'axios';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useLocation } from 'react-router-dom';
import BarChartIcon from '@mui/icons-material/BarChart';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import AccountSettings from './AccountSettings';
import useDashboardStripeReturn from './useDashboardStripeReturn';

const statusOptions = [
    { value: 'available', label: 'Available', color: '#4caf50' },
    { value: 'busy', label: 'Busy', color: '#ff9800' },
    { value: 'away', label: 'Away', color: '#bdbdbd' },
    { value: 'do-not-disturb', label: 'Do Not Disturb', color: '#d32f2f' },
];

const notificationMeta = type => {
    switch (type) {
        case 'team_invite':
            return { label: 'Team Invite', icon: <PersonAddAltOutlinedIcon fontSize="small" />, color: '#1976d2' };
        case 'team_added':
            return { label: 'Team Update', icon: <GroupOutlinedIcon fontSize="small" />, color: '#2e7d32' };
        case 'team_removed':
            return { label: 'Team Update', icon: <GroupOutlinedIcon fontSize="small" />, color: '#d32f2f' };
        case 'project_assigned':
            return { label: 'Project Assigned', icon: <AssignmentTurnedInOutlinedIcon fontSize="small" />, color: '#6a1b9a' };
        case 'autotranslate_complete':
            return { label: 'Autotranslate Done', icon: <CheckCircleOutlineIcon fontSize="small" />, color: '#00796b' };
        default:
            return { label: 'Notification', icon: <NotificationsIcon fontSize="small" />, color: '#455a64' };
    }
};

const formatNotificationTime = dateString => new Date(dateString).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
});

function StatusDot({ isOnline, status, sx = {} }) {
    let color = '#bdbdbd';
    if (isOnline) {
        const opt = statusOptions.find(o => o.value === status) || statusOptions[0];
        color = opt.color;
    }
    return <FiberManualRecordIcon fontSize="small" sx={{ color, ...sx }} />;
}

function TranslationDashboard() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { token, user } = useSelector(state => state.auth);

    const [profileData, setProfileData] = useState(user || {});
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Profile menu anchors
    const [anchorEl, setAnchorEl] = useState(null);
    const profileMenuOpen = Boolean(anchorEl);
    const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
    const statusMenuOpen = Boolean(statusMenuAnchorEl);

    // Notifications state
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [notifAnchorEl, setNotifAnchorEl] = useState(null);
    const notifMenuOpen = Boolean(notifAnchorEl);
    const [settingsOpen, setSettingsOpen] = useState(false);

    useDashboardStripeReturn({
        onCheckoutSuccess: () => setSettingsOpen(true),
    });

    // User statuses
    const [userStatuses, setUserStatuses] = useState({});
    const [statusUpdating, setStatusUpdating] = useState(false);

    // Derive current user's status, default to online
    const statusInfo = userStatuses[profileData._id] ?? { isOnline: true, status: profileData.status || 'available' };
    const isOnline = statusInfo.isOnline;
    const currentStatus = statusInfo.status;

    const { pathname } = useLocation();
    const isTranslatePage = /projects\/[^/]+\/translate/.test(pathname);

    // Helpers for avatar
    const avatarUrl = u => {
        if (!u.avatarUrl) return '';
        const file = u.avatarUrl.split('/').pop();
        return `/api/users/avatars/${u._id}/${file}`;
    };
    const getInitials = name => {
        const [first, last] = name.split(' ');
        return `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase();
    };

    useEffect(() => {
        if (user) {
            setProfileData(user);
        }
    }, [user]);

    // Fetch notifications
    useEffect(() => {
        if (!token) return;
        axios.get('/api/notifications', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                setNotifications(res.data);
                setUnreadCount(res.data.filter(n => !n.read).length);
            })
            .catch(err => console.error('Error fetching notifications:', err));
    }, [token]);

    const fetchChatUnread = useCallback(async () => {
        if (!token) {
            setChatUnreadCount(0);
            return;
        }
        try {
            const [directRes, groupRes] = await Promise.all([
                axios.get('/api/team-chat/unread-total', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/group-chat/unread-total', { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            const directCount = Number(directRes.data?.count || 0);
            const groupCount = Number(groupRes.data?.count || 0);
            setChatUnreadCount(directCount + groupCount);
        } catch (err) {
            console.error('Error fetching chat unread count:', err);
        }
    }, [token]);

    useEffect(() => {
        fetchChatUnread();
    }, [fetchChatUnread]);

    // Notification menu handlers
    const handleNotifMenuOpen = e => setNotifAnchorEl(e.currentTarget);
    const handleNotifMenuClose = () => setNotifAnchorEl(null);

    const handleNotificationClick = notifId => {
        const targetNotification = notifications.find(n => n._id === notifId);
        const wasUnread = targetNotification ? !targetNotification.read : false;

        axios.patch(`/api/notifications/${notifId}/read`, {}, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => {
                setNotifications(prev => prev.map(n => n._id === notifId ? { ...n, read: true } : n));
                if (wasUnread) {
                    setUnreadCount(prev => Math.max(prev - 1, 0));
                }
            })
            .catch(err => console.error('Error marking notification as read:', err));
        handleNotifMenuClose();
    };

    const handleClearNotifications = () => {
        axios.patch('/api/notifications/read-all', {}, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => {
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                setUnreadCount(0);
            })
            .catch(err => console.error('Error marking all notifications as read:', err));
    };

    // Fetch profile data
    useEffect(() => {
        if (!token) return;
        axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setProfileData(res.data))
            .catch(err => console.error('Error fetching profile data:', err));
    }, [token]);

    // Profile menu handlers
    const handleProfileMenuOpen = e => setAnchorEl(e.currentTarget);
    const handleProfileMenuClose = () => {
        setAnchorEl(null);
        setStatusMenuAnchorEl(null);
    };
    const handleStatusMenuOpen = e => setStatusMenuAnchorEl(e.currentTarget);
    const handleStatusMenuClose = () => setStatusMenuAnchorEl(null);
    const handleLogout = () => {
        dispatch(logoutAction());
        navigate('/');
    };

    // Change status
    const handleChangeStatus = status => {
        setStatusUpdating(true);
        axios.put('/api/users/status', { status }, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => {
                setUserStatuses(prev => ({
                    ...prev,
                    [profileData._id]: { isOnline: true, status }
                }));
                if (window.__SOCKET__ && profileData._id) {
                    window.__SOCKET__.emit('setStatus', { userId: profileData._id, status });
                }
            })
            .catch(err => console.error('Error updating status:', err))
            .finally(() => setStatusUpdating(false));
    };

    // Initial fetch of all users' statuses
    useEffect(() => {
        if (!token) return;
        axios.get('/api/users/status', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setUserStatuses(res.data))
            .catch(err => console.error('Error fetching statuses:', err));
    }, [token]);

    // Real-time subscription to status updates
    useEffect(() => {
        const sock = window.__SOCKET__;
        if (!sock) return;
        const handler = ({ userId, isOnline, status }) => {
            setUserStatuses(prev => ({
                ...prev,
                [userId]: { isOnline, status }
            }));
        };
        sock.on('statusUpdate', handler);
        return () => sock.off('statusUpdate', handler);
    }, []);

    useEffect(() => {
        const sock = window.__SOCKET__;
        if (!sock || !profileData?._id) return;
        const currentUserId = String(profileData._id);

        const handleTeamMessage = msg => {
            const receiverId = msg?.receiverId?._id || msg?.receiverId;
            const senderId = msg?.senderId?._id || msg?.senderId;
            if (!receiverId || String(receiverId) !== currentUserId) return;
            if (senderId && String(senderId) === currentUserId) return;
            fetchChatUnread();
        };

        const handleGroupMessage = msg => {
            const senderId = msg?.senderId?._id || msg?.senderId;
            if (senderId && String(senderId) === currentUserId) return;
            fetchChatUnread();
        };

        sock.on('receiveTeamMessage', handleTeamMessage);
        sock.on('receiveGroupMessage', handleGroupMessage);
        return () => {
            sock.off('receiveTeamMessage', handleTeamMessage);
            sock.off('receiveGroupMessage', handleGroupMessage);
        };
    }, [profileData?._id, fetchChatUnread]);

    // Menu definitions
    const mainMenuItems = [
        { text: 'Dashboard', icon: <HomeIcon />, path: '' },
        { text: 'Translation Projects', icon: <FolderIcon />, path: 'projects' },
        { text: 'Start New Translation', icon: <TranslateIcon />, path: 'new-translation' },
        { text: 'Team', icon: <GroupIcon />, path: 'team' },
        { text: 'Apps', icon: <AppsIcon />, path: 'apps' },
        { text: 'Support Center', icon: <HelpOutlineIcon />, path: 'support' },
        { text: 'Chat', icon: <ChatIcon />, path: 'chat' },
        { text: 'Jobs Board', icon: <AssignmentTurnedInOutlinedIcon />, path: 'jobs' },
        { text: 'File Manager', icon: <InsertDriveFileIcon />, path: 'file-manager' },  // <--- Added here
        { text: 'Analytics', icon: <BarChartIcon />, path: 'analytics' },
    ];
    const languageSettingsMenu = [
        { text: 'Language Settings', icon: <SettingsIcon />, path: 'language-settings' },
    ];
    let adminMenuItems = [];
    if (hasAdminPrivileges(user)) {
        adminMenuItems = [
            { text: 'Admin Dashboard', icon: <SettingsIcon />, path: 'admin' },
            { text: 'User Management', icon: <GroupIcon />, path: 'admin/users' },
            { text: 'System Logs', icon: <FolderIcon />, path: 'admin/logs' },
            { text: 'AI Usage', icon: <TranslateIcon />, path: 'admin/ai-usage' },
            { text: 'Support Manager', icon: <HelpOutlineIcon />, path: 'admin/support' },
            { text: 'Support Tickets', icon: <SupportAgentIcon />, path: 'admin/support-tickets' },
            { text: 'Utilities', icon: <BuildIcon />, path: 'admin/utilities' },
        ];
    }

    const drawerWidth = 240;
    const collapsedWidth = 60;
    const appBarBg = '#38414a';
    const drawerBg = '#f0f4ff';

    const activeColor = theme.palette.primary.main;
    const activeBg = alpha(activeColor, 0.12);
    const hoverBg = alpha(activeColor, 0.08);
    const iconActiveBg = alpha(activeColor, 0.2);
    const inactiveIconColor = alpha(theme.palette.text.primary, 0.6);

    const toggleSidebar = () => setIsSidebarCollapsed(prev => !prev);

    const buildDashboardPath = path => (path ? `/dashboard/${path}` : '/dashboard');

    const isPathActive = targetPath => {
        const fullPath = buildDashboardPath(targetPath);
        if (fullPath === '/dashboard') {
            return pathname === '/dashboard' || pathname === '/dashboard/' || pathname === '/dashboard/home';
        }
        return pathname === fullPath || pathname.startsWith(`${fullPath}/`);
    };

    const renderMenuIcon = (item, isActive) => {
        const baseIcon = React.cloneElement(item.icon, {
            sx: {
                transition: 'color 0.3s ease',
                color: 'inherit',
                ...(item.icon.props?.sx || {}),
            },
        });

        const iconWrapper = (
            <Box
                sx={{
                    width: isSidebarCollapsed ? 36 : 32,
                    height: isSidebarCollapsed ? 36 : 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: isActive ? iconActiveBg : 'transparent',
                    transition: 'background-color 0.3s ease, color 0.3s ease',
                }}
            >
                {baseIcon}
            </Box>
        );

        if (item.path === 'chat') {
            const badgeContent = chatUnreadCount > 99 ? '99+' : chatUnreadCount;
            return (
                <Badge
                    color="error"
                    badgeContent={badgeContent}
                    invisible={!chatUnreadCount}
                    overlap="circular"
                >
                    {iconWrapper}
                </Badge>
            );
        }

        return iconWrapper;
    };

    const renderMenuItem = item => {
        const fullPath = buildDashboardPath(item.path);
        const isActive = isPathActive(item.path);
        return (
            <ListItem
                button
                key={item.text}
                component={Link}
                to={fullPath}
                selected={isActive}
                sx={{
                    position: 'relative',
                    mx: 1,
                    my: 0.5,
                    borderRadius: 2,
                    color: isActive ? activeColor : theme.palette.text.primary,
                    '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: activeBg,
                        color: activeColor,
                    },
                    '&:hover': {
                        backgroundColor: isActive ? activeBg : hoverBg,
                        color: activeColor,
                    },
                    '&:hover .MuiListItemIcon-root': {
                        color: activeColor,
                    },
                    '&.Mui-selected .MuiListItemIcon-root .MuiBox-root': {
                        backgroundColor: iconActiveBg,
                    },
                    '&:hover .MuiListItemIcon-root .MuiBox-root': {
                        backgroundColor: iconActiveBg,
                    },
                    ...(isSidebarCollapsed
                        ? {
                            justifyContent: 'center',
                            px: 1,
                        }
                        : {}),
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: isSidebarCollapsed ? 'auto' : 40,
                        display: 'flex',
                        justifyContent: 'center',
                        color: isActive ? activeColor : inactiveIconColor,
                        mr: isSidebarCollapsed ? 0 : 2,
                        transition: 'color 0.3s ease',
                        '& .MuiSvgIcon-root': {
                            color: 'inherit',
                            transition: 'color 0.3s ease',
                        },
                        '& .MuiBadge-root': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        },
                        '& .MuiBadge-root .MuiSvgIcon-root': {
                            color: 'inherit',
                            transition: 'color 0.3s ease',
                        },
                    }}
                >
                    {renderMenuIcon(item, isActive)}
                </ListItemIcon>
                {!isSidebarCollapsed && (
                    <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                            sx: {
                                fontWeight: isActive ? 700 : 600,
                                color: 'inherit',
                                transition: 'color 0.3s ease',
                            },
                        }}
                    />
                )}
            </ListItem>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />

                {/* Sidebar Drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        width: isSidebarCollapsed ? collapsedWidth : drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: isSidebarCollapsed ? collapsedWidth : drawerWidth,
                            boxSizing: 'border-box',
                            backgroundColor: drawerBg,
                            borderRight: '1px solid #e0e0e0',
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 64,
                            borderBottom: '1px solid #e0e0e0',
                            background: drawerBg,
                        }}
                    >
                        {isSidebarCollapsed ? (
                            <img src="/assets/logo/logo-icon.png" alt="Logo" style={{ height: 36 }} />
                        ) : (
                            <img src="/assets/logo/logo-crop.png" alt="Logo" style={{ width: '60%' }} />
                        )}
                    </Box>

                    <List>
                        {mainMenuItems.map(renderMenuItem)}
                    </List>

                    <Divider />
                    <List>
                        {languageSettingsMenu.map(renderMenuItem)}
                    </List>

                    {adminMenuItems.length > 0 && (
                        <>
                            <Divider />
                            <List subheader={!isSidebarCollapsed && (
                                <Typography sx={{ pl: 2, pt: 1, fontWeight: 600 }}>Admin</Typography>
                            )}>
                                {adminMenuItems.map(renderMenuItem)}
                            </List>
                        </>
                    )}
                </Drawer>

                {/* Top AppBar */}
                <AppBar
                    position="fixed"
                    color="default"
                    elevation={1}
                    sx={{
                        zIndex: theme.zIndex.drawer + 1,
                        backgroundColor: appBarBg,
                        borderBottom: '1px solid #e0e0e0',
                        width: `calc(100% - ${isSidebarCollapsed ? collapsedWidth : drawerWidth}px)`,
                        ml: `${isSidebarCollapsed ? collapsedWidth : drawerWidth}px`,
                    }}
                >
                    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: 64 }}>
                        <IconButton color="inherit" onClick={toggleSidebar} edge="start" sx={{ mr: 2 }}>
                            {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                        <Box sx={{ flexGrow: 1 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* Notifications */}
                            <IconButton color="inherit" onClick={handleNotifMenuOpen}>
                                <Badge badgeContent={unreadCount} color="error">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                            <Menu
                                anchorEl={notifAnchorEl}
                                open={notifMenuOpen}
                                onClose={handleNotifMenuClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ sx: { width: 420, maxHeight: 420 } }}
                            >
                                <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography fontWeight="bold">Notifications</Typography>
                                    {notifications.length > 0 && (
                                        <Typography
                                            variant="body2"
                                            onClick={handleClearNotifications}
                                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                                        >
                                            Clear All
                                        </Typography>
                                    )}
                                </Box>
                                <Divider />
                                {notifications.length === 0
                                    ? <MenuItem disabled>No notifications</MenuItem>
                                    : notifications.slice(0, 7).map(n => {
                                        const meta = notificationMeta(n.type);
                                        return (
                                            <MenuItem
                                                key={n._id}
                                                onClick={() => handleNotificationClick(n._id)}
                                                sx={{
                                                    alignItems: 'flex-start',
                                                    gap: 1.5,
                                                    py: 1.25,
                                                    whiteSpace: 'normal',
                                                    backgroundColor: n.read ? 'inherit' : '#eef7ff'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        mt: 0.4,
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: alpha(meta.color, 0.12),
                                                        color: meta.color,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {meta.icon}
                                                </Box>
                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: n.read ? 600 : 700 }}>
                                                            {meta.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                            {formatNotificationTime(n.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        {n.message}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })
                                }
                                {notifications.length > 7 && (
                                    <>
                                        <Divider />
                                        <MenuItem disabled>(Showing latest 7)</MenuItem>
                                    </>
                                )}
                            </Menu>

                            {/* Profile + Settings + Status */}
                            <Box sx={{ ml: 2 }}>
                                <IconButton color="inherit" onClick={handleProfileMenuOpen}>
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        badgeContent={
                                            <StatusDot
                                                isOnline={isOnline}
                                                status={currentStatus}
                                            />
                                        }
                                    >
                                        {profileData.avatarUrl ? (
                                            <Avatar src={avatarUrl(profileData)} />
                                        ) : profileData.firstName ? (
                                            <Avatar>{getInitials(`${profileData.firstName} ${profileData.lastName}`)}</Avatar>
                                        ) : (
                                            <AccountCircle />
                                        )}
                                    </Badge>
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={profileMenuOpen}
                                    onClose={handleProfileMenuClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <MenuItem onClick={handleStatusMenuOpen}>
                                        Status
                                        <ChevronRightIcon fontSize="small" sx={{ ml: 'auto' }} />
                                    </MenuItem>
                                    <Menu
                                        anchorEl={statusMenuAnchorEl}
                                        open={statusMenuOpen}
                                        onClose={handleStatusMenuClose}
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    >
                                        {statusOptions.map(opt => (
                                            <MenuItem
                                                key={opt.value}
                                                selected={opt.value === currentStatus}
                                                onClick={() => { handleChangeStatus(opt.value); handleStatusMenuClose(); handleProfileMenuClose(); }}
                                                disabled={statusUpdating}
                                                sx={{ color: opt.color }}
                                            >
                                                <StatusDot isOnline={true} status={opt.value} sx={{ mr: 1 }} />
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                    <Divider />
                                    <MenuItem onClick={() => { setSettingsOpen(true); handleProfileMenuClose(); }}>
                                        Account Settings
                                    </MenuItem>
                                    <MenuItem onClick={() => { navigate('/dashboard/profile'); handleProfileMenuClose(); }}>
                                        Profile
                                    </MenuItem>
                                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                                </Menu>
                            </Box>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* Main Content */}
                <Box component="main" sx={{ flexGrow: 1, p: isTranslatePage ? 0 : 3 }}>
                    <Toolbar />
                    <Outlet context={{ refreshChatUnread: fetchChatUnread }} />
                </Box>
                <AccountSettings isDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
            </Box>
        </ThemeProvider>
    );
}

export default TranslationDashboard;
